import { useState, useEffect, useCallback } from 'react';
import { Shield, Search, FileText, Globe, Link2, Loader2 } from 'lucide-react';
import type { ADGPO } from './adTypes';
import baseapi from '../../services/baseapi';
import { Pagination } from '../InternalScan/InternalScanDashboard/Pagination';

interface ADGPOViewProps {
    gpos?: ADGPO[];
    asset_id?: string | number;
}

export default function ADGPOView({ gpos: initialGpos, asset_id: propAssetId }: ADGPOViewProps) {
    const info = JSON.parse(sessionStorage.getItem('internal_ids') || '{}');
    const asset_id = propAssetId || info.asset_id;

    const [gpos, setGpos] = useState<ADGPO[]>(initialGpos || []);
    const [totalRecords, setTotalRecords] = useState<number>(initialGpos?.length || 0);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 350);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch API with skip & limit
    const fetchGpos = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = { is_deleted: false };
            if (asset_id) queryObj['asset_id'] = asset_id;

            if (debouncedSearch.trim()) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { display_name: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { domain: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { gpo_status: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { linked_to: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch.trim()}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
            };

            const response = await baseapi.get('/ad_gpos', { params });
            if (response.data && response.data.status) {
                setGpos(response.data.data || []);
                setTotalRecords(response.data.total ?? response.data.data?.length ?? 0);
            } else if (response.data?.data) {
                setGpos(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            }
        } catch (error: any) {
            console.error('Failed to fetch GPOs:', error);
        } finally {
            setLoading(false);
        }
    }, [asset_id, debouncedSearch, currentPage, itemsPerPage]);

    useEffect(() => {
        fetchGpos();
    }, [fetchGpos]);

    const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;

    const getStatusBadge = (status?: string) => {
        if (!status) return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300">Enabled</span>;
        const lower = status.toLowerCase();
        if (lower === 'allsettingsenabled') {
            return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300">All Enabled</span>;
        }
        if (lower === 'allsettingsdisabled') {
            return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 text-red-700 border border-red-500/30 dark:bg-red-500/20 dark:text-red-300">Disabled</span>;
        }
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-700 border border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300">Partial</span>;
    };

    const formatLinkedTo = (link?: string) => {
        if (!link) return '—';
        const parts = link.split(',').map(p => p.trim());
        const first = parts[0];
        if (/^DC=/i.test(first)) return '(Domain Root)';
        return first.replace(/^OU=/i, '') || link;
    };

    return (
        <div className="w-full bg-white dark:bg-gray-900/80 border border-slate-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col">
            {/* Header / Toolbar */}
            <div className="flex items-center justify-between p-3 bg-slate-50/90 dark:bg-gray-900/90 border-b border-slate-200 dark:border-gray-800 backdrop-blur-md">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-orange-500/10 dark:bg-orange-500/20 border border-orange-500/30 rounded-md">
                        <Shield className="text-orange-500 dark:text-orange-400 w-4 h-4" />
                    </div>
                    <div>
                        <h2 className="text-xs font-bold text-slate-900 dark:text-white tracking-wide">Group Policy Objects</h2>
                        <span className="text-[9px] text-slate-500 dark:text-gray-400 uppercase tracking-widest font-mono">
                            {totalRecords} {totalRecords === 1 ? 'GPO' : 'GPOs'} Detected
                        </span>
                    </div>
                </div>

                <div className="relative w-56 group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search GPOs or links..."
                        className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-md pl-8 pr-3 py-1 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/60 transition-all"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-x-auto custom-scroll">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-100 dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 sticky top-0 z-10">
                        <tr className="text-slate-600 dark:text-gray-400 uppercase text-[9px] tracking-widest font-bold">
                            <th className="p-2.5">GPO Name</th>
                            <th className="p-2.5">Status</th>
                            <th className="p-2.5">Domain</th>
                            <th className="p-2.5">Linked Target</th>
                            <th className="p-2.5">Created</th>
                            <th className="p-2.5">Modified</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-gray-800">
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="py-16 text-center text-slate-500 dark:text-gray-400 font-mono">
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin text-orange-500 dark:text-orange-400" />
                                        <span>Loading GPOs...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : gpos.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="py-16 text-center text-slate-500 dark:text-gray-500 font-mono">
                                    No Group Policy Objects found.
                                </td>
                            </tr>
                        ) : (
                            gpos.map((gpo) => (
                                <tr key={gpo.object_guid} className="hover:bg-slate-50 dark:hover:bg-gray-900/60 transition-colors group">
                                    <td className="p-2.5 font-bold text-slate-900 dark:text-white max-w-xs">
                                        <div className="flex items-center gap-2">
                                            <FileText size={13} className="text-orange-500 dark:text-orange-400 shrink-0" />
                                            <div>
                                                <span className="block truncate">{gpo.display_name || gpo.name}</span>
                                                <span className="text-[9px] text-slate-500 dark:text-gray-500 font-mono block truncate">{gpo.name}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-2.5">{getStatusBadge(gpo.gpo_status)}</td>
                                    <td className="p-2.5 font-mono text-slate-700 dark:text-gray-300">
                                        <div className="flex items-center gap-1">
                                            <Globe size={11} className="text-blue-500 dark:text-blue-400" />
                                            {gpo.domain || '—'}
                                        </div>
                                    </td>
                                    <td className="p-2.5 font-mono text-amber-700 dark:text-amber-300">
                                        <div className="flex items-center gap-1" title={gpo.linked_to}>
                                            <Link2 size={11} className="text-amber-500 dark:text-amber-400" />
                                            {formatLinkedTo(gpo.linked_to)}
                                        </div>
                                    </td>
                                    <td className="p-2.5 font-mono text-slate-600 dark:text-gray-400">
                                        {gpo.gpo_created_time ? gpo.gpo_created_time.split('T')[0] : '—'}
                                    </td>
                                    <td className="p-2.5 font-mono text-slate-600 dark:text-gray-400">
                                        {gpo.gpo_modified_time ? gpo.gpo_modified_time.split('T')[0] : '—'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalRecords}
                onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                }}
                itemsPerPageOptions={[5, 10, 25, 50]}
            />
        </div>
    );
}
